import { getSchoolTimezoneOffset, isSchoolsReady } from "state/teachers/selectors";

/**
 * PRIVATE
 * Inject some computed properties into messages
 * @returns {Immutable.Map}
 */
function buildMessage(state, message, isPending) {
  message = message.toMap().set("pending", isPending);
  if (!isPending) {
    const readMessageIDs = state.getIn([ "teachers", "readMessageIDs" ]);
    const isMessageUnread = message.get("sender") === "school" && !readMessageIDs.includes(message.get("id"))
    const timezoneOffset = getSchoolTimezoneOffset(state, message.get("schoolID"));
    message = message
      .set("unread", isMessageUnread)
      .set("timezoneOffset", timezoneOffset);
  }
  return message;
}

/**
 * Get properties of the message (or pending reply) with the given ID
 * @returns {Immutable.Map}
 */
export function getMessageByID(state, messageID) {
  const pendingReplyIDRegex = /\d+:P:\d+/;
  const isPending = pendingReplyIDRegex.test(messageID);
  let message;
  if (isPending) {
    message = state.getIn(["teachers", "pendingReplies", messageID]);
  } else {
    message = state.getIn( ['teachers', 'messages', messageID] );
  }
  return buildMessage(state, message, isPending);
}

/**
 * Determine if there are messages (excl. pending replies) for the given school
 * @returns {boolean}
 */
export function hasMessagesForSchool(state, schoolID) {
  const messageList = state.getIn(["teachers", "messages"])
    .filter((message) => message.get("schoolID") === schoolID);
  return messageList.size > 0;
}

/**
 * Get IDs of all messages (incl. pending) for the given school, sorted by sentDate
 * @returns {Immutable.Seq}
 */
export function getSchoolMessageIDList(state, schoolID) {
  // Get messages retreived from server
  const messages = state.getIn(['teachers', 'messages'])
    // filter for given school
    .filter((message) => message.get("schoolID") === schoolID)
    // sort by timestamp
    .sortBy((message) => message.get("sentDate"))
  // Get messages not yet on server
  const pendingReplies = getSchoolPendingReplies(state, schoolID);
  // Append pending messages to the main list
  return messages.keySeq().concat(pendingReplies.keySeq()).reverse();
}

/**
 * Get IDs of pending replies for the given school
 * @returns {Immutable.Seq}
 */
export function getSchoolPendingReplyIDs(state, schoolID) {
  return getSchoolPendingReplies(state, schoolID).keySeq();
}

/**
 * Get pending replies for the given school
 * @returns {Immutable.Map}
 */
export function getSchoolPendingReplies(state, schoolID) {
  return state.getIn(["teachers", "pendingReplies"])
    .filter((pendingReply) => pendingReply.get("schoolID") === schoolID);
}

/**
 * Get the saved replyDraft for the given school
 * @returns {string}
 */
export function getSchoolReplyDraft(state, schoolID) {
  return state.getIn(["teachers", "replyDrafts", schoolID], "");
}

/**
 * Get IDs of given school's messages that have been read before
 * @returns {Immutable.List}
 */
export function getReadMessageIDs(state, schoolID) {
  return state.getIn([ "teachers", "readMessageIDs" ]);
}

/**
 * @returns {boolean}
 */
export function isMessagesLoading(state) {
  return state.getIn(["teachers", "isMessagesLoading"]);
}

/**
 * isMessagesReady - returns true if notices are loaded and ready
 * @returns {boolean}
 */
export function isMessagesReady(state, schoolID) {
  const hasMessages = hasMessagesForSchool(state, schoolID);
  const canDisplayMessages = hasMessages || !isMessagesLoading(state);
  return isSchoolsReady(state) && canDisplayMessages;
}

/**
 * used by home page "Recent Messages" section
 * @returns {Immutable.List}
 */
export function getRecentMessages(state, count) {
  return state.getIn(["teachers", "messages"])
    .filter(message => message.get("sender") === "school")
    .sortBy(message => message.get("sentDate"))
    .takeLast(count)
    .reverse()
    .toList()
}

/**
 * Get the current "going to" messageID.
 * @returns {string}
 */
export function getGoToMessageID(state) {
  return state.getIn(["teachers", "goToMessageID"]);
}

/**
 * Get the received messages for the given school.
 * @returns {Immutable.List}
 */
export function getSchoolReceivedMessageIDList(state, schoolID) {
  return state.getIn(['teachers', 'messages'])
    .filter(message => message.schoolID === schoolID)
    .filter(message => message.sender === "school")
    .sortBy(message => message.sentDate)
    .keySeq().toList();
}



// WEBPACK FOOTER //
// ./src/state/teachers/selectors/messages.js